import React, { useState, useCallback, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import InlineScanner from '@/components/InlineScanner';
import { unlockAudio } from '@/lib/scanFeedback';
import BarcodeAutocomplete from '@/components/BarcodeAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { getCachedGarment, markCachedSold, queuePending, saveLocalSale } from '@/lib/offlineDb';
import { uid, money, money2, PAYMENT_METHODS } from '@/lib/helpers';
import { TagCombobox } from '@/components/TagCombobox';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { fieldsFor, validateCustom, cleanCustomValues } from '@/lib/customFields';
import { useQuery } from '@tanstack/react-query';
import { ScanLine, X, Loader2, ShoppingCart, ChevronDown, ChevronUp, Pencil,
  CheckCircle2, Printer, MessageCircle, Plus, Banknote, Smartphone, CreditCard,
  Building2, Clock, User, Sun, Moon, ArrowLeftRight, Store, PackagePlus, Sparkles, Wallet } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const METHOD_ICON = { CASH: Banknote, UPI: Smartphone, CARD: CreditCard, BANK: Building2, CREDIT: Clock };

export default function Billing() {
  const { user, currentStore, stores, online, refreshPending, settings, mode, toggleMode, can, selectStore } = useApp();
  const navigate = useNavigate();
  const upiAccounts = settings?.payment_accounts?.UPI || [];
  const cardAccounts = settings?.payment_accounts?.CARD || [];
  // Owner-configurable billing behaviour (falls back to everything enabled).
  const billingOpts = settings?.billing_options || {};
  const allowedMethods = PAYMENT_METHODS.filter((m) => (billingOpts.methods || {})[m] !== false);
  const firstMethod = allowedMethods[0] || 'CASH';
  const allowItemDiscount = billingOpts.allow_item_discount !== false;
  const allowBillDiscount = billingOpts.allow_bill_discount !== false;
  const [upiAccount, setUpiAccount] = useState(null);
  const [cardAccount, setCardAccount] = useState(null);
  const recentScan = useRef({});

  const [scanOpen, setScanOpen] = useState(false);
  const [cart, setCart] = useState([]);
  // Cross-store + quick item-create (from Billing) state.
  const canTransfer = can('transfer_create');
  const canCreateItem = user?.role === 'OWNER' || user?.role === 'MANAGER' || !!user?.owner_access;
  const [otherStoreItem, setOtherStoreItem] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const blankItem = () => ({ barcode: '', product: '', price: '', cost: '', size: '', colour: '', category: '' });
  const [createForm, setCreateForm] = useState(blankItem());
  // Recently created items (from Billing) for one-tap re-add. Persisted locally.
  const RECENT_KEY = 'shabz.recent_created_items';
  const [recentItems, setRecentItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  });
  const rememberCreated = useCallback((it) => {
    setRecentItems((prev) => {
      const next = [{ barcode: it.barcode, product: it.product, price: it.price },
        ...prev.filter((r) => r.barcode !== it.barcode)].slice(0, 8);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
      return next;
    });
  }, []);
  const clearRecent = useCallback(() => {
    setRecentItems([]);
    try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
  }, []);
  const defaultBillDiscount = String(settings?.receipt?.default_bill_discount || '');
  const [billDiscount, setBillDiscount] = useState('');
  const [billDiscMode, setBillDiscMode] = useState('amt');
  const [billDiscPct, setBillDiscPct] = useState('');
  const billDiscountTouched = useRef(false);
  // Pre-fill the owner's default bill discount on a fresh, untouched bill.
  React.useEffect(() => {
    if (!billDiscountTouched.current && cart.length === 0 && defaultBillDiscount) {
      setBillDiscount(defaultBillDiscount);
    }
  }, [defaultBillDiscount, cart.length]);
  const [payOpen, setPayOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [addlOpen, setAddlOpen] = useState(false);
  const [splitMode, setSplitMode] = useState(false);
  const [salesperson, setSalesperson] = useState(user?.name || user?.username || '');
  // Optional per-item "Sold by" mode for bulk / end-of-day entry.
  const [perItemSeller, setPerItemSeller] = useState(false);
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [billDate, setBillDate] = useState(todayStr());
  // Keep today's time-of-day for a same-day bill; otherwise stamp the chosen date at noon.
  const billDateToIso = (d) => (d === todayStr() ? new Date().toISOString() : new Date(`${d}T12:00:00`).toISOString());
  const [customer, setCustomer] = useState({ name: '', phone: '' });
  const billingFields = fieldsFor(settings?.custom_fields, 'billing');
  const [customVals, setCustomVals] = useState({});
  const [customErrs, setCustomErrs] = useState({});
  const [singleMethod, setSingleMethod] = useState('CASH');
  const [payments, setPayments] = useState([{ method: 'UPI', amount: '' }]);
  const [completed, setCompleted] = useState(null);

  const canEditPrice = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const canPickSalesperson = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const { data: staff = [] } = useQuery({
    queryKey: ['billing-staff'], enabled: canPickSalesperson,
    queryFn: async () => (await api.get('/incentives/staff')).data.data,
  });
  const staffOptions = React.useMemo(() => {
    const names = (staff || []).map((s) => s.name).filter(Boolean);
    const seen = new Set(names.map((n) => n.toLowerCase()));
    const me = user?.name || user?.username;
    if (me && !seen.has(me.toLowerCase())) names.unshift(me);
    return names;
  }, [staff, user]);

  const subtotal = cart.reduce((s, i) => s + (Number(i.price) || 0), 0);
  const itemDiscount = cart.reduce((s, i) => s + (Number(i.discount) || 0), 0);
  // Keep the flat bill discount in sync when entered as a percentage.
  React.useEffect(() => {
    if (billDiscMode === 'pct') {
      const base = Math.max(0, subtotal - itemDiscount);
      billDiscountTouched.current = true;
      setBillDiscount(String(Math.round(base * (Number(billDiscPct) || 0) / 100)));
    }
  }, [billDiscMode, billDiscPct, subtotal, itemDiscount]);
  const grandTotal = Math.max(0, subtotal - itemDiscount - (Number(billDiscount) || 0));
  const storeName = stores.find((s) => s.id === currentStore)?.name || 'Store';
  const otherStoreName = otherStoreItem ? (stores.find((s) => s.id === otherStoreItem.store_id)?.name || 'another store') : '';

  // ---------- SCAN / ADD ----------
  const addToCart = useCallback((g) => {
    setCart((c) => (c.some((i) => i.barcode === g.barcode)
      ? c
      : [{ ...g, origPrice: g.price, discount: '', showDisc: false, editPrice: false }, ...c]));
    toast.success(`${g.product || g.barcode} · ${money(g.price)} added`);
    // Dismiss the on-screen keyboard after adding so the list/summary is visible.
    try { setTimeout(() => { document.activeElement && document.activeElement.blur && document.activeElement.blur(); }, 20); } catch (e) { /* ignore */ }
  }, []);

  const openCreateItem = (barcode = '') => {
    if (!canCreateItem) { toast.error('You do not have permission to create items.'); return; }
    if (!online) { toast.error('Creating a new item needs an internet connection.'); return; }
    setCreateForm({ ...blankItem(), barcode: String(barcode || '').toUpperCase() });
    setCreateOpen(true);
  };

  const addByBarcode = useCallback(async (raw) => {
    const barcode = String(raw).trim().toUpperCase();
    if (!barcode) return;
    // debounce repeated camera detections of same code
    const t = Date.now();
    if (recentScan.current[barcode] && t - recentScan.current[barcode] < 1500) return;
    recentScan.current[barcode] = t;
    if (cart.some((i) => i.barcode === barcode)) { toast.error(`${barcode} is already in the bill.`); return; }
    let g = null;
    if (online) {
      try { g = (await api.get(`/garments/lookup/${encodeURIComponent(barcode)}`)).data.data; }
      catch (e) {
        if (e?.response && e.response.status === 404) { g = null; }
        else if (e?.response) { toast.error(errMsg(e)); return; }
        else { g = await getCachedGarment(barcode); }
      }
    } else { g = await getCachedGarment(barcode); }
    if (!g) {
      // Not found anywhere → offer to create it (managers, online only).
      if (canCreateItem && online) { openCreateItem(barcode); return; }
      toast.error('Barcode not found.');
      return;
    }
    if (g.status === 'SOLD') { toast.error(`ITEM ALREADY SOLD — ${barcode} cannot be billed again.`); return; }
    if (g.status === 'IN_TRANSIT') { toast.error(`${barcode} is in transit and not available.`); return; }
    if (g.store_id !== currentStore) { setOtherStoreItem(g); return; }  // ask: transfer here / bill from that store
    addToCart(g);
  }, [cart, online, currentStore, canCreateItem, addToCart]);

  // Instantly move the item to the current store, then add it to the bill.
  const transferHereAndAdd = async () => {
    const g = otherStoreItem;
    if (!g) return;
    setTransferring(true);
    try {
      await api.post('/transfers', { from_store: g.store_id, to_store: currentStore, barcodes: [g.barcode], immediate: true, client_txn_id: uid() });
      addToCart({ ...g, store_id: currentStore });
      toast.success(`Transferred to ${storeName} & added`);
      setOtherStoreItem(null);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTransferring(false); }
  };

  // Switch the whole bill to the item's store (kept single-store per bill).
  const billFromOtherStore = () => {
    const g = otherStoreItem;
    if (!g) return;
    if (cart.length > 0) { toast.error('Finish or clear the current bill before billing from another store.'); return; }
    selectStore(g.store_id);
    addToCart(g);
    setOtherStoreItem(null);
  };

  const saveNewItem = async () => {
    const bc = createForm.barcode.trim().toUpperCase();
    if (!bc) { toast.error('Enter a code / barcode.'); return; }
    if (!createForm.price) { toast.error('Enter a selling price.'); return; }
    setCreating(true);
    try {
      await api.post('/stock/intake', { garments: [{
        barcode: bc, product: createForm.product.trim(), price: Number(createForm.price),
        cost: createForm.cost ? Number(createForm.cost) : null,
        size: createForm.size.trim(), colour: createForm.colour.trim(), category: createForm.category.trim(),
        store_id: currentStore,
      }], client_txn_id: uid() });
      addToCart({ barcode: bc, product: createForm.product.trim(), price: Number(createForm.price),
        cost: createForm.cost ? Number(createForm.cost) : null, size: createForm.size.trim(),
        colour: createForm.colour.trim(), category: createForm.category.trim(), store_id: currentStore, status: 'AVAILABLE' });
      rememberCreated({ barcode: bc, product: createForm.product.trim(), price: Number(createForm.price) });
      toast.success('Item created & added to bill');
      setCreateOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCreating(false); }
  };

  const removeItem = (barcode) => setCart((c) => c.filter((i) => i.barcode !== barcode));
  const patchItem = (barcode, patch) => setCart((c) => c.map((i) => {
    if (i.barcode !== barcode) return i;
    const next = { ...i, ...patch };
    // In % mode, keep the flat discount in sync with price/percentage.
    if (next.discMode === 'pct' && ('price' in patch || 'discPct' in patch || 'discMode' in patch)) {
      next.discount = Math.round((Number(next.price) || 0) * (Number(next.discPct) || 0) / 100);
    }
    return next;
  }));

  // One-tap bulk: set "Sold by" for every cart line at once (faster counter billing).
  const assignAllSellers = (v) => {
    const seller = v === '__invoice__' ? '' : v;
    setCart((c) => c.map((i) => ({ ...i, seller })));
    toast.success(seller ? `All items assigned to ${seller}` : 'All items set to bill salesperson');
  };
  const setItemDiscMode = (i, mode) => {
    if (mode === 'pct') {
      const pct = (i.discPct != null && i.discPct !== '')
        ? i.discPct
        : (Number(i.price) > 0 ? String(Math.round(((Number(i.discount) || 0) / Number(i.price)) * 10000) / 100) : '');
      patchItem(i.barcode, { discMode: 'pct', discPct: pct });
    } else {
      patchItem(i.barcode, { discMode: 'amt' });
    }
  };

  // ---------- PAYMENT ----------
  const openPayment = () => {
    if (!cart.length) { toast.error('Scan a garment to start billing.'); return; }
    setSplitMode(false); setSingleMethod(firstMethod); setUpiAccount(null); setCardAccount(null);
    setPayments([{ method: firstMethod, amount: String(grandTotal) }]);
    setPayOpen(true);
  };

  const paidTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const remaining = grandTotal - paidTotal;
  const updatePayment = (idx, field, value) => setPayments((p) => p.map((x, i) => (i === idx ? { ...x, [field]: value } : x)));
  const addPaymentRow = () => setPayments((p) => [...p, { method: firstMethod, amount: String(Math.max(0, remaining)) }]);
  const removePaymentRow = (idx) => setPayments((p) => p.filter((_, i) => i !== idx));

  const completeBill = async () => {
    let cleanPayments;
    const hasCredit = splitMode ? payments.some((p) => p.method === 'CREDIT') : singleMethod === 'CREDIT';
    if (splitMode) {
      cleanPayments = payments.filter((p) => p.method === 'CREDIT' || Number(p.amount) > 0)
        .map((p) => ({ method: p.method, amount: Number(p.amount) || 0 }));
      const pt = cleanPayments.reduce((s, p) => s + p.amount, 0);
      if (!hasCredit && Math.abs(pt - grandTotal) > 0.5) { toast.error(`Payment (${money(pt)}) must match total (${money(grandTotal)}).`); return; }
    } else {
      if (singleMethod === 'UPI' && upiAccounts.length && !upiAccount) { toast.error('Select which UPI account received the payment.'); return; }
      if (singleMethod === 'CARD' && cardAccounts.length && !cardAccount) { toast.error('Select which card machine was used.'); return; }
      const acct = singleMethod === 'UPI' ? upiAccount?.name : singleMethod === 'CARD' ? cardAccount?.name : null;
      cleanPayments = [{ method: singleMethod, amount: singleMethod === 'CREDIT' ? 0 : grandTotal, account: acct }];
    }
    if (hasCredit && !customer.name && !customer.phone) { toast.error('Customer name or mobile is required for a credit sale.'); return; }
    const cErrs = validateCustom(settings?.custom_fields, 'billing', customVals);
    if (Object.keys(cErrs).length) {
      setCustomErrs(cErrs);
      toast.error('Please fill the required custom fields before completing the bill.');
      return;
    }
    setCustomErrs({});
    const client_txn_id = uid();
    const payload = {
      client_txn_id, store_id: currentStore, salesperson,
      items: cart.map((i) => {
        const it = { barcode: i.barcode, discount: allowItemDiscount ? (Number(i.discount) || 0) : 0 };
        if (canEditPrice && Number(i.price) !== Number(i.origPrice)) it.price = Number(i.price) || 0;
        if (perItemSeller && canPickSalesperson && i.seller) it.salesperson = i.seller;
        return it;
      }),
      bill_discount: allowBillDiscount ? (Number(billDiscount) || 0) : 0, payments: cleanPayments,
      customer: (customer.name || customer.phone) ? customer : null,
      created_at: billDateToIso(billDate),
      custom: cleanCustomValues(settings?.custom_fields, 'billing', customVals),
    };
    setProcessing(true);
    try {
      if (online) {
        const r = await api.post('/sales', payload);
        finishSuccess(r.data.data.invoice_no, grandTotal, false, null);
      } else { await saveOffline(payload); }
    } catch (e) {
      if (e?.response) toast.error(errMsg(e));
      else await saveOffline(payload);
    } finally { setProcessing(false); }
  };

  const saveOffline = async (payload) => {
    await queuePending({ client_txn_id: payload.client_txn_id, kind: 'sale', data: payload });
    const localInv = `OFFLINE-${payload.client_txn_id.slice(0, 6).toUpperCase()}`;
    await saveLocalSale({
      client_txn_id: payload.client_txn_id, invoice_no: localInv, store_id: currentStore,
      salesperson, customer: payload.customer,
      items: cart.map((i) => ({ barcode: i.barcode, product: i.product, size: i.size, colour: i.colour,
        salesperson: (perItemSeller && canPickSalesperson && i.seller) ? i.seller : undefined,
        price: Number(i.price) || 0, discount: Number(i.discount) || 0, final: (Number(i.price) || 0) - (Number(i.discount) || 0) })),
      net: grandTotal, payments: payload.payments, paid: payload.payments.reduce((s, p) => s + p.amount, 0),
      created_at: payload.created_at, synced: false, offline: true,
    });
    for (const i of cart) await markCachedSold(i.barcode);
    await refreshPending();
    toast.success('Bill saved offline. It will sync automatically.');
    finishSuccess(localInv, grandTotal, true, payload.client_txn_id);
  };

  const finishSuccess = (invoice_no, amount, offline, txn) => {
    setPayOpen(false);
    setCompleted({ invoice_no, amount, offline, txn });
  };

  const newBill = () => {
    setCart([]); billDiscountTouched.current = false; setBillDiscount(defaultBillDiscount); setBillDiscMode('amt'); setBillDiscPct(''); setCustomer({ name: '', phone: '' });
    setPayments([{ method: 'UPI', amount: '' }]); setSplitMode(false); setMoreOpen(false);
    setSalesperson(user?.name || user?.username || '');
    setBillDate(todayStr());
    setCustomVals({}); setCustomErrs({});
    setCompleted(null);
  };

  const shareWhatsapp = () => {
    const phone = customer?.phone ? `91${customer.phone}` : '';
    const text = `*SHABZ CLOSET*%0AInvoice ${completed.invoice_no}%0A${storeName}%0ATotal: ${money(completed.amount)}%0AThank you!`;
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  // ---------- SUCCESS SCREEN ----------
  if (completed) {
    return (
      <Page>
        <TopBar title="Bill Completed" showStore={false} />
        <div className="flex flex-col items-center px-4 pt-10 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-100">
            <CheckCircle2 className="h-11 w-11 text-emerald-600" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-bold" data-testid="bill-success-title">Bill Completed</h2>
          <p className="mt-1 font-mono text-sm text-muted-foreground" data-testid="bill-success-invoice">{completed.invoice_no}</p>
          <p className="mt-3 font-mono text-4xl font-bold tabular-nums">{money2(completed.amount)}</p>
          {completed.offline && <p className="mt-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">Saved offline · will sync automatically</p>}

          <div className="mt-8 w-full max-w-sm space-y-3">
            <Button data-testid="bill-success-new" onClick={newBill} className="h-14 w-full rounded-2xl text-lg font-semibold">
              <Plus className="mr-2 h-5 w-5" /> New Bill
            </Button>
            {!completed.offline && can('billing_edit') && (
              <Button variant="outline" data-testid="bill-success-edit" onClick={() => navigate(`/invoice/${completed.invoice_no}/edit`)}
                className="h-12 w-full rounded-xl font-semibold">
                <Pencil className="mr-2 h-4 w-4" /> Edit Bill
              </Button>
            )}
            <div className="grid grid-cols-2 gap-3">
              {!completed.offline && (
                <Button variant="secondary" className="h-12 rounded-xl" data-testid="bill-success-view"
                  onClick={() => navigate(`/invoice/${completed.invoice_no}`)}><Printer className="mr-2 h-4 w-4" />Print</Button>
              )}
              {completed.offline && (
                <Button variant="secondary" className="h-12 rounded-xl" data-testid="bill-success-view"
                  onClick={() => navigate(`/invoice/local-${completed.txn}`)}><Printer className="mr-2 h-4 w-4" />View</Button>
              )}
              <Button variant="secondary" className="h-12 rounded-xl" data-testid="bill-success-whatsapp" onClick={shareWhatsapp}>
                <MessageCircle className="mr-2 h-4 w-4" />WhatsApp</Button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  // ---------- BILLING SCREEN ----------
  return (
    <Page>
      <TopBar title="New Bill" right={canCreateItem ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="billing-quick-add-trigger" aria-label="Quick add"
              className="flex h-9 items-center gap-1 rounded-full border border-primary/60 bg-primary/10 px-2.5 text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              <Plus className="h-4 w-4" /><span className="hidden text-xs font-semibold uppercase tracking-wide sm:inline">Quick Add</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Quick add</DropdownMenuLabel>
            <DropdownMenuItem data-testid="billing-qa-stock" onClick={() => navigate('/stock')}><PackagePlus className="mr-2 h-4 w-4 text-primary" />Add Stock</DropdownMenuItem>
            <DropdownMenuItem data-testid="billing-qa-purchase" onClick={() => navigate('/purchase')}><ShoppingCart className="mr-2 h-4 w-4 text-primary" />Purchase</DropdownMenuItem>
            <DropdownMenuItem data-testid="billing-qa-expense" onClick={() => navigate('/expenses')}><Wallet className="mr-2 h-4 w-4 text-amber-500" />Expense</DropdownMenuItem>
            <DropdownMenuItem data-testid="billing-qa-transfer" onClick={() => navigate('/transfers')}><ArrowLeftRight className="mr-2 h-4 w-4 text-primary" />Transfer</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null} />
      <div className="px-3 pt-3 pb-44 sm:px-4 sm:pt-4">
        {/* STORE / USER / CONNECTION INDICATOR */}
        <div className="mb-2.5 flex items-center justify-between gap-2 rounded-2xl bg-slate-900 px-3 py-2.5 text-white" data-testid="billing-context-bar">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-400 font-display text-sm font-bold text-slate-900">{storeName[0]}</span>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-semibold">{storeName}</p>
              <p className="truncate text-[11px] text-slate-300">Counter · {user?.name || user?.username}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${online ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`} data-testid="billing-connection">
              <span className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-emerald-400' : 'bg-amber-400'}`} />{online ? 'Online' : 'Offline'}
            </span>
            <button onClick={toggleMode} data-testid="billing-theme-toggle" aria-label="Toggle light/dark mode"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20">
              {mode === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* SOLD BY — pinned at the top for quick attribution */}
        <div className="mb-2.5 flex items-center gap-2 rounded-2xl border border-border bg-card p-2.5" data-testid="billing-soldby-top">
          <User className="h-4 w-4 shrink-0 text-primary" />
          <span className="shrink-0 text-sm font-medium">Sold by</span>
          <div className="ml-auto w-44 max-w-[55%]">
            {canPickSalesperson ? (
              <Select value={salesperson} onValueChange={setSalesperson}>
                <SelectTrigger data-testid="billing-salesperson" className="h-10 rounded-xl"><SelectValue placeholder="Salesperson" /></SelectTrigger>
                <SelectContent>{staffOptions.map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}</SelectContent>
              </Select>
            ) : <Input value={user?.name || user?.username} disabled className="h-10 rounded-xl" data-testid="billing-salesperson-locked" />}
          </div>
        </div>

        {/* SCAN / SEARCH — most prominent */}
        <div className="rounded-2xl border border-border bg-card p-3 shadow-sm">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <ScanLine className="h-3.5 w-3.5 text-primary" /> Scan or search to add items
          </p>
          <Button data-testid="billing-scan-barcode-button" onClick={() => { unlockAudio(); setScanOpen((v) => !v); }}
            variant={scanOpen ? 'secondary' : 'default'}
            className="h-16 w-full rounded-xl text-lg font-semibold shadow-sm transition-transform active:scale-[0.99]">
            {scanOpen ? <><X className="mr-2 h-6 w-6" /> Close Scanner</> : <><ScanLine className="mr-2 h-6 w-6" /> Scan Barcode</>}
          </Button>
          {scanOpen && (
            <InlineScanner onDetected={(code) => addByBarcode(code)} onClose={() => setScanOpen(false)} />
          )}
          <div className="mt-3">
            <BarcodeAutocomplete storeId={currentStore} statusFilter="AVAILABLE" testId="billing-manual-barcode-input"
              placeholder="Search item / type code" autoFocus
              onSelect={(g) => addByBarcode(g.barcode)} onSubmit={(code) => addByBarcode(code)} />
          </div>
          <Button data-testid="billing-paste-whatsapp-button" variant="secondary"
            onClick={() => navigate('/paste-whatsapp-sales')}
            className="mt-3 h-12 w-full rounded-xl font-semibold">
            <MessageCircle className="mr-2 h-5 w-5 text-emerald-500" /> Paste WhatsApp Sales
          </Button>
          {canCreateItem && (
            <button type="button" onClick={() => openCreateItem('')} data-testid="billing-new-item-button"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
              <PackagePlus className="h-3.5 w-3.5" /> New item not in stock? Create &amp; add
            </button>
          )}
          {canCreateItem && recentItems.length > 0 && (
            <div className="mt-3 border-t border-border pt-3" data-testid="billing-recent-items">
              <div className="mb-1.5 flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-primary" /> Recently created
                </p>
                <button type="button" onClick={clearRecent} data-testid="billing-recent-clear"
                  className="text-[11px] font-medium text-muted-foreground underline hover:text-foreground">Clear</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentItems.map((r) => (
                  <button key={r.barcode} type="button" onClick={() => addByBarcode(r.barcode)}
                    data-testid={`billing-recent-item-${r.barcode}`}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary hover:bg-primary/10 active:scale-[0.98]">
                    <Plus className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <span className="truncate">{r.product || r.barcode}</span>
                    <span className="shrink-0 font-mono text-muted-foreground">{money(r.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ITEMS */}
        <div className="mt-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Items ({cart.length})</h2>
            <div className="flex items-center gap-3">
              {canPickSalesperson && cart.length > 0 && (
                <button type="button" onClick={() => setPerItemSeller((v) => !v)} data-testid="billing-peritem-seller-toggle"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${perItemSeller ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                  <User className="h-3 w-3" /> Sold by per item
                </button>
              )}
              {cart.length > 0 && <button data-testid="billing-clear-cart" onClick={newBill} className="text-xs text-muted-foreground underline">Clear</button>}
            </div>
          </div>
          {perItemSeller && canPickSalesperson && cart.length > 0 && (
            <div className="mb-2 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-2.5 py-1.5" data-testid="billing-bulk-seller">
              <User className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="text-[11px] font-medium text-muted-foreground">Assign all to</span>
              <Select value="" onValueChange={assignAllSellers}>
                <SelectTrigger data-testid="billing-bulk-seller-select" className="h-8 flex-1 rounded-lg text-xs"><SelectValue placeholder="Choose salesperson…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__invoice__">Same as bill{salesperson ? ` (${salesperson})` : ''}</SelectItem>
                  {staffOptions.map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-14 text-center">
              <ShoppingCart className="h-9 w-9 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">Scan a garment to start</p>
              <p className="mt-1 text-xs text-muted-foreground">Scan → item added → scan next</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid="billing-cart-list">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b border-border bg-secondary/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Item</span><span className="w-20 text-right">Price</span><span className="w-8" />
              </div>
              {cart.map((i) => {
                const lineAmount = (Number(i.price) || 0) - (Number(i.discount) || 0);
                const overridden = Number(i.price) !== Number(i.origPrice);
                return (
                  <div key={i.barcode} data-testid={`cart-item-${i.barcode}`} className="border-b border-border px-3 py-2.5 last:border-0">
                    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{i.product}</p>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Available
                          </span>
                          <p className="truncate font-mono text-[11px] text-muted-foreground">{i.barcode}{i.size ? ` · ${i.size}` : ''}</p>
                        </div>
                      </div>
                      <div className="w-20 text-right">
                        <p className="font-mono text-sm font-semibold tabular-nums">{money(lineAmount)}</p>
                        {(overridden || Number(i.discount) > 0) && (
                          <p className="font-mono text-[11px] text-muted-foreground line-through">{money(i.origPrice)}</p>
                        )}
                      </div>
                      <button data-testid={`cart-remove-${i.barcode}`} onClick={() => removeItem(i.barcode)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Remove ${i.barcode}`}><X className="h-4 w-4" /></button>
                    </div>
                    {/* compact line actions */}
                    <div className="mt-1 flex items-center gap-3">
                      {allowItemDiscount && (i.showDisc ? (
                        <div className="flex items-center gap-1.5">
                          <div className="flex overflow-hidden rounded-lg border border-border">
                            <button type="button" onClick={() => setItemDiscMode(i, 'amt')} data-testid={`cart-disc-mode-amt-${i.barcode}`}
                              className={`px-2 py-1 text-xs font-semibold ${i.discMode !== 'pct' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>₹</button>
                            <button type="button" onClick={() => setItemDiscMode(i, 'pct')} data-testid={`cart-disc-mode-pct-${i.barcode}`}
                              className={`px-2 py-1 text-xs font-semibold ${i.discMode === 'pct' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>%</button>
                          </div>
                          {i.discMode === 'pct' ? (
                            <>
                              <Input inputMode="decimal" value={i.discPct ?? ''} autoFocus
                                onChange={(e) => patchItem(i.barcode, { discPct: e.target.value })}
                                placeholder="0" className="h-8 w-14 rounded-lg text-right text-sm" data-testid={`cart-discount-pct-${i.barcode}`} />
                              <span className="text-xs text-muted-foreground" data-testid={`cart-discount-calc-${i.barcode}`}>% = {money(Number(i.discount) || 0)}</span>
                            </>
                          ) : (
                            <Input inputMode="numeric" value={i.discount} autoFocus
                              onChange={(e) => patchItem(i.barcode, { discount: e.target.value })}
                              placeholder="0" className="h-8 w-20 rounded-lg text-right text-sm" data-testid={`cart-discount-${i.barcode}`} />
                          )}
                        </div>
                      ) : (
                        <button onClick={() => patchItem(i.barcode, { showDisc: true })} className="text-xs font-medium text-primary" data-testid={`cart-add-discount-${i.barcode}`}>+ discount</button>
                      ))}
                      {canEditPrice && (i.editPrice ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">Price ₹</span>
                          <Input inputMode="numeric" value={i.price} autoFocus
                            onChange={(e) => patchItem(i.barcode, { price: e.target.value })}
                            onBlur={() => patchItem(i.barcode, { editPrice: false, price: Number(i.price) || i.origPrice })}
                            className="h-8 w-24 rounded-lg text-right text-sm" data-testid={`cart-price-${i.barcode}`} />
                        </div>
                      ) : (
                        <button onClick={() => patchItem(i.barcode, { editPrice: true })}
                          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary"
                          data-testid={`cart-edit-price-${i.barcode}`}><Pencil className="h-3 w-3" />edit price</button>
                      ))}
                      {perItemSeller && canPickSalesperson && (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3 w-3 shrink-0 text-muted-foreground" />
                          <Select value={i.seller || '__invoice__'} onValueChange={(v) => patchItem(i.barcode, { seller: v === '__invoice__' ? '' : v })}>
                            <SelectTrigger data-testid={`cart-seller-${i.barcode}`} className="h-8 w-36 rounded-lg text-xs"><SelectValue placeholder="Sold by" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__invoice__">Same as bill{salesperson ? ` (${salesperson})` : ''}</SelectItem>
                              {staffOptions.map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CUSTOMER & SOLD BY — small optional section on the bill screen */}
        <button onClick={() => setMoreOpen((a) => !a)}
          className="mt-4 flex w-full items-center justify-between rounded-xl bg-secondary/60 px-3 py-2.5 text-sm font-medium"
          data-testid="billing-more-toggle">
          <span className="inline-flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" />
            Personal information <span className="text-xs font-normal text-muted-foreground">(optional)</span></span>
          <span className="inline-flex items-center gap-2">
            {(customer.name || customer.phone) && <span className="max-w-[110px] truncate text-xs text-muted-foreground">{customer.name || customer.phone}</span>}
            {moreOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>
        {moreOpen && (
          <div className="mt-2 space-y-3 rounded-xl border border-border bg-card p-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Customer name</Label><Input data-testid="billing-customer-name" value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} placeholder="Optional" className="mt-1 h-11 rounded-xl" /></div>
              <div><Label className="text-xs">Mobile</Label><Input data-testid="billing-customer-phone" value={customer.phone} inputMode="numeric" onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} placeholder="Optional" className="mt-1 h-11 rounded-xl" /></div>
            </div>
            <div>
              <Label className="text-xs">Bill date {billDate !== todayStr() && <span className="text-amber-600">(back-dated)</span>}</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="date" value={billDate} max={todayStr()} onChange={(e) => setBillDate(e.target.value || todayStr())} className="h-11 rounded-xl" data-testid="billing-date" />
                {billDate !== todayStr() && <Button type="button" variant="ghost" size="sm" className="h-9 rounded-lg text-xs" onClick={() => setBillDate(todayStr())} data-testid="billing-date-today">Today</Button>}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Defaults to today. Change it to record a bill on a past date.</p>
            </div>
          </div>
        )}

        {billingFields.length > 0 && (
          <>
            <button onClick={() => setAddlOpen((a) => !a)}
              className="mt-3 flex w-full items-center justify-between rounded-xl bg-secondary/60 px-3 py-2.5 text-sm font-medium"
              data-testid="billing-addl-toggle">
              <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-muted-foreground" />
                Additional details <span className="text-xs font-normal text-muted-foreground">(optional)</span></span>
              {addlOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {addlOpen && (
              <div className="mt-2 rounded-xl border border-border bg-card p-3" data-testid="billing-custom-fields">
                <CustomFieldsForm defs={settings?.custom_fields} context="billing" value={customVals}
                  onChange={setCustomVals} errors={customErrs} idPrefix="billing-cf" />
              </div>
            )}
          </>
        )}
      </div>

      {/* TOTALS + COLLECT PAYMENT */}
      {cart.length > 0 && (
        <div className="fixed inset-x-0 bottom-16 z-40 border-t border-border bg-card/95 px-4 py-2 backdrop-blur lg:bottom-0 lg:left-[var(--sidebar-width,15rem)]"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
          <div className="mx-auto max-w-6xl space-y-1">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal ({cart.length} item{cart.length !== 1 ? 's' : ''})</span><span className="font-mono tabular-nums" data-testid="billing-subtotal">{money2(subtotal)}</span></div>
            {itemDiscount > 0 && (
              <div className="flex justify-between text-sm text-emerald-700"><span>Item discounts</span><span className="font-mono tabular-nums" data-testid="billing-item-discount">− {money2(itemDiscount)}</span></div>
            )}
            {allowBillDiscount && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Bill discount</span>
              <div className="flex items-center gap-1.5">
                <div className="flex overflow-hidden rounded-lg border border-border">
                  <button type="button" onClick={() => setBillDiscMode('amt')} data-testid="billing-bill-disc-mode-amt"
                    className={`px-2 py-1 text-xs font-semibold ${billDiscMode !== 'pct' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>₹</button>
                  <button type="button" data-testid="billing-bill-disc-mode-pct"
                    onClick={() => { const base = Math.max(0, subtotal - itemDiscount); if (billDiscMode !== 'pct' && !billDiscPct) { setBillDiscPct(base > 0 ? String(Math.round(((Number(billDiscount) || 0) / base) * 10000) / 100) : ''); } setBillDiscMode('pct'); }}
                    className={`px-2 py-1 text-xs font-semibold ${billDiscMode === 'pct' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>%</button>
                </div>
                {billDiscMode === 'pct' ? (
                  <>
                    <Input inputMode="decimal" value={billDiscPct} onChange={(e) => setBillDiscPct(e.target.value)} placeholder="0" className="h-8 w-14 rounded-lg text-right text-sm" data-testid="billing-bill-discount-pct" />
                    <span className="text-xs text-muted-foreground" data-testid="billing-bill-discount-calc">% = {money(Number(billDiscount) || 0)}</span>
                  </>
                ) : (
                  <Input inputMode="numeric" value={billDiscount} onChange={(e) => { billDiscountTouched.current = true; setBillDiscount(e.target.value); }} placeholder="0" className="h-8 w-24 rounded-lg text-right text-sm" data-testid="billing-bill-discount" />
                )}
              </div>
            </div>
            )}
            <div className="mt-1 flex items-center justify-between rounded-xl bg-slate-900 px-3 py-2 text-white">
              <div><p className="text-[10px] uppercase tracking-wide text-slate-300">Grand Total</p><p data-testid="billing-grand-total" className="font-mono text-xl font-bold tabular-nums">{money2(grandTotal)}</p></div>
              <Button data-testid="billing-collect-payment-button" onClick={openPayment} className="h-11 rounded-xl bg-amber-400 px-6 text-sm font-semibold text-slate-900 hover:bg-amber-300">Collect Payment</Button>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT */}
      <Drawer open={payOpen} onOpenChange={setPayOpen}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Payment</DrawerTitle></DrawerHeader>
          <div className="max-h-[75vh] overflow-y-auto px-4 pb-6">
            <div className="rounded-xl bg-primary p-4 text-center text-primary-foreground">
              <p className="text-xs opacity-80">Total to collect</p>
              <p className="font-mono text-3xl font-bold tabular-nums">{money2(grandTotal)}</p>
            </div>

            {!splitMode ? (
              <>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {allowedMethods.map((m) => {
                    const Icon = METHOD_ICON[m];
                    const active = singleMethod === m;
                    return (
                      <button key={m} data-testid={`pay-method-${m}`} onClick={() => { setSingleMethod(m); setUpiAccount(null); setCardAccount(null); }}
                        className={`flex flex-col items-center justify-center gap-1 rounded-xl border py-3 text-sm font-medium ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>
                        <Icon className="h-5 w-5" />{m}
                      </button>
                    );
                  })}
                </div>
                {singleMethod === 'UPI' && (
                  <div className="mt-3">
                    <Label className="text-xs">Select UPI account</Label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {upiAccounts.map((a) => (
                        <button key={a.id} data-testid={`pay-upi-${a.name}`} onClick={() => setUpiAccount(a)}
                          className={`rounded-full border px-3 py-1.5 text-sm font-medium ${upiAccount?.id === a.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{a.name}</button>
                      ))}
                    </div>
                    {upiAccount && (
                      <div className="mt-3 flex flex-col items-center rounded-xl border border-border bg-white p-4" data-testid="pay-upi-qr">
                        {upiAccount.qr_image ? (
                          <img src={upiAccount.qr_image} alt={`${upiAccount.name} QR`} className="h-44 w-44 object-contain" />
                        ) : upiAccount.upi_id ? (
                          <QRCodeSVG value={`upi://pay?pa=${encodeURIComponent(upiAccount.upi_id.trim())}&pn=${encodeURIComponent(upiAccount.name)}&am=${Number(grandTotal).toFixed(2)}&cu=INR&tn=${encodeURIComponent('SHABZ CLOSET')}`} size={176} includeMargin />
                        ) : (
                          <p className="py-8 text-center text-xs text-muted-foreground">No QR set for {upiAccount.name}. Add a UPI ID or upload the QR image from your bank/UPI app in Settings → Payment.</p>
                        )}
                        <p className="mt-2 text-sm font-semibold">{upiAccount.name}</p>
                        <p className="text-xs text-muted-foreground">{upiAccount.qr_image ? `Scan & pay ${money(grandTotal)}` : `Amount locked · Scan to pay ${money(grandTotal)}`}</p>
                        {upiAccount.qr_image && <p className="mt-0.5 text-[10px] text-amber-700">Uploaded QR: customer enters the amount. For auto-locked amount, add this bank's UPI ID instead.</p>}
                      </div>
                    )}
                  </div>
                )}
                {singleMethod === 'CARD' && (
                  <div className="mt-3">
                    <Label className="text-xs">Select card machine</Label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {cardAccounts.map((a) => (
                        <button key={a.id} data-testid={`pay-card-${a.name}`} onClick={() => setCardAccount(a)}
                          className={`rounded-full border px-3 py-1.5 text-sm font-medium ${cardAccount?.id === a.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{a.name}</button>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={() => { setSplitMode(true); setPayments([{ method: firstMethod, amount: String(grandTotal) }]); }}
                  className="mt-3 text-xs font-medium text-primary" data-testid="billing-add-split">Split payment across methods</button>
              </>
            ) : (
              <div className="mt-4">
                <div className="flex items-center justify-between"><Label>Split payment</Label>
                  <button onClick={() => setSplitMode(false)} className="text-xs text-muted-foreground underline">Use single method</button></div>
                <div className="mt-2 space-y-2">
                  {payments.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Select value={p.method} onValueChange={(v) => updatePayment(idx, 'method', v)}>
                        <SelectTrigger data-testid={`payment-method-${idx}`} className="h-11 w-[120px] rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>{allowedMethods.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input inputMode="numeric" value={p.amount} data-testid={`payment-amount-${idx}`} onChange={(e) => updatePayment(idx, 'amount', e.target.value)} placeholder="Amount" className="h-11 rounded-xl" />
                      {payments.length > 1 && <button data-testid={`payment-remove-${idx}`} onClick={() => removePaymentRow(idx)} className="flex h-11 w-11 items-center justify-center rounded-xl border border-border"><X className="h-4 w-4" /></button>}
                    </div>
                  ))}
                </div>
                <button data-testid="payment-add-row" onClick={addPaymentRow} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary"><Plus className="h-3 w-3" />Add method</button>
                <div className="mt-2 flex justify-between text-sm"><span className="text-muted-foreground">Paid</span><span className="font-mono tabular-nums">{money2(paidTotal)}</span></div>
                {Math.abs(remaining) > 0.5 && !payments.some((p) => p.method === 'CREDIT') && (
                  <div className={`flex justify-between text-sm ${remaining > 0 ? 'text-amber-700' : 'text-rose-700'}`}><span>{remaining > 0 ? 'Remaining' : 'Excess'}</span><span className="font-mono tabular-nums">{money2(Math.abs(remaining))}</span></div>
                )}
              </div>
            )}

            {/* CREDIT needs a customer — ask inline, no extra screens */}
            {(splitMode ? payments.some((p) => p.method === 'CREDIT') : singleMethod === 'CREDIT') && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                <p className="text-xs font-medium text-amber-800">Credit sale — customer details required</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Input data-testid="billing-credit-customer-name" value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} placeholder="Customer name" className="h-11 rounded-xl bg-card" />
                  <Input data-testid="billing-credit-customer-phone" value={customer.phone} inputMode="numeric" onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} placeholder="Mobile" className="h-11 rounded-xl bg-card" />
                </div>
              </div>
            )}

            <Button data-testid="billing-complete-bill-button" onClick={completeBill} disabled={processing} className="mt-5 h-14 w-full rounded-xl text-lg font-semibold">
              {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : `Complete Bill · ${money(grandTotal)}`}
            </Button>
            {!online && <p className="mt-2 text-center text-xs text-amber-700">Offline — bill will be saved and synced later.</p>}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Item is in another store — choose how to proceed */}
      <Drawer open={!!otherStoreItem} onOpenChange={(o) => { if (!o) setOtherStoreItem(null); }}>
        <DrawerContent className="bg-card" data-testid="other-store-drawer">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Item is in {otherStoreName}</DrawerTitle></DrawerHeader>
          <div className="space-y-3 px-4 pb-6">
            {otherStoreItem && (
              <div className="flex items-center gap-3 rounded-xl border border-border p-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><Store className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{otherStoreItem.product || otherStoreItem.barcode}</p>
                  <p className="truncate font-mono text-xs text-muted-foreground">{otherStoreItem.barcode} · {money(otherStoreItem.price)}</p>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">This garment isn&apos;t in {storeName}. Move it here for this sale, or bill it from {otherStoreName}.</p>

            {canTransfer && (
              <Button className="h-12 w-full rounded-xl" onClick={transferHereAndAdd} disabled={transferring} data-testid="other-store-transfer">
                {transferring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowLeftRight className="mr-2 h-4 w-4" />}
                Transfer to {storeName} &amp; add
              </Button>
            )}
            <Button variant="secondary" className="h-12 w-full rounded-xl" onClick={billFromOtherStore} data-testid="other-store-switch">
              <Store className="mr-2 h-4 w-4" /> Bill from {otherStoreName}
            </Button>
            {!canTransfer && <p className="text-center text-[11px] text-muted-foreground">You don&apos;t have permission to transfer stock. You can still bill it from {otherStoreName}.</p>}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Quick create a new item from Billing */}
      <Drawer open={createOpen} onOpenChange={setCreateOpen}>
        <DrawerContent className="bg-card" data-testid="create-item-drawer">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Create item</DrawerTitle></DrawerHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto px-4 pb-6">
            <div className="grid grid-cols-2 gap-2">
              <BField label="Code / Barcode *"><Input data-testid="create-item-barcode" value={createForm.barcode} onChange={(e) => setCreateForm({ ...createForm, barcode: e.target.value })} className="h-11 rounded-xl uppercase" /></BField>
              <BField label="Selling price *"><Input inputMode="numeric" data-testid="create-item-price" value={createForm.price} onChange={(e) => setCreateForm({ ...createForm, price: e.target.value })} className="h-11 rounded-xl" /></BField>
              <BField label="Garment name"><Input data-testid="create-item-name" value={createForm.product} onChange={(e) => setCreateForm({ ...createForm, product: e.target.value })} className="h-11 rounded-xl" /></BField>
              <BField label="Category"><TagCombobox value={createForm.category} onChange={(v) => setCreateForm({ ...createForm, category: v })} placeholder="Select / create" testId="create-item-category" allowCreate={canCreateItem} /></BField>
              <BField label="Size"><Input value={createForm.size} onChange={(e) => setCreateForm({ ...createForm, size: e.target.value })} className="h-11 rounded-xl" /></BField>
              <BField label="Colour"><Input value={createForm.colour} onChange={(e) => setCreateForm({ ...createForm, colour: e.target.value })} className="h-11 rounded-xl" /></BField>
              <BField label="Cost (Purchase price)"><Input inputMode="numeric" data-testid="create-item-cost" value={createForm.cost} onChange={(e) => setCreateForm({ ...createForm, cost: e.target.value })} className="h-11 rounded-xl" /></BField>
              <BField label="Store">
                <Input value={storeName} readOnly disabled className="h-11 rounded-xl" data-testid="create-item-store" />
              </BField>
            </div>
            <Button data-testid="create-item-save" className="h-12 w-full rounded-xl" onClick={saveNewItem} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create & add to bill'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </Page>
  );
}

const BField = ({ label, children }) => (
  <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);
